<script lang="ts">
import { computed, defineComponent, h, inject, mergeProps, provide, ref, unref, watch } from 'vue';

import { classNames, getUid, pick } from '@components/utils';

import {
  DEFAULT_MENU_OFFSET,
  makeDelayProps,
  makeDimensionProps,
  makeLazyProps,
  makeOverlayProps,
  makeThemeProps,
  makeTransitionProps,
  provideTheme,
  useProxyModel,
  useRender,
  useScopeId
} from '../../composable';
import { CdsOverlay } from '../CdsOverlay';
import { makeActivatorProps, makeLocationStrategyProps, makeScrollStrategyProps } from '../CdsOverlay/composable';

import { CdsMenuSymbol, MENU_CLOSE_DELAY, MENU_OPEN_DELAY } from './shared';

import type { PropType } from 'vue';

export const componentName = 'CdsMenu';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Уникальный идентификатор
     */
    id: {
      type: String as PropType<string>,
      default: undefined
    },
    ...makeOverlayProps({
      scrim: false
    }),
    ...makeActivatorProps({
      closeOnContentClick: true
    }),
    ...makeDelayProps({
      openDelay: MENU_OPEN_DELAY,
      closeDelay: MENU_CLOSE_DELAY
    }),
    ...makeLocationStrategyProps({
      locationStrategy: 'connected' as const,
      offset: DEFAULT_MENU_OFFSET
    }),
    ...makeScrollStrategyProps({
      scrollStrategy: 'none' as const
    }),
    ...makeLazyProps(),
    ...makeDimensionProps(),
    ...makeTransitionProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @type { boolean }
     */
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { slots, emit, expose }) {
    const { themeClasses } = provideTheme(props);
    const isActive = useProxyModel(props, 'modelValue');
    const { scopeId } = useScopeId();

    const uid = getUid();
    const id = computed(() => props.id || `cds-menu-${uid}`);

    const overlay = ref<typeof CdsOverlay>();

    const parent = inject(CdsMenuSymbol, null);
    const openChildren = ref(0);

    provide(CdsMenuSymbol, {
      register() {
        ++openChildren.value;
      },
      unregister() {
        --openChildren.value;
      },
      closeParents() {
        setTimeout(() => {
          if (!openChildren.value) {
            isActive.value = false;
            parent?.closeParents();
          }
        });
      }
    });

    watch(isActive, (value) => {
      value ? parent?.register() : parent?.unregister();
    });

    function onClickOutside() {
      parent?.closeParents();
    }

    const activatorPropsAndAttrs = computed(() =>
      mergeProps(
        {
          'aria-haspopup': 'menu',
          'aria-expanded': String(unref(isActive)),
          'aria-owns': unref(id)
        },
        props.activatorProps
      )
    );

    useRender(() => {
      const [overlayProps] = pick(props, Object.keys(CdsOverlay.props) as any);

      return h(
        CdsOverlay,
        {
          ref: overlay,
          class: classNames('cds-menu', unref(themeClasses)),
          ...overlayProps,
          modelValue: unref(isActive),
          absolute: true,
          activatorProps: unref(activatorPropsAndAttrs),
          'onClick:outside': onClickOutside,
          'onUpdate:modelValue': (value) => {
            isActive.value = value;
          },
          ...scopeId
        },
        {
          activator: slots.activator,
          default: slots.default
        }
      );
    });

    expose({
      overlay,
      openChildren
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsMenu';
</style>
